/** Default avatar for guest (and all) community comments. */
export const DOCTOR_TOKEN_AVATAR = "/revive/doctor-token-sticker.png";

export const COMMENT_TEXT_MIN = 2;
export const COMMENT_TEXT_MAX = 400;
export const COMMENT_NAME_MAX = 24;
export const COMMENT_AVATAR_MAX = 500;
export const DEFAULT_COMMENT_NAME = "Guest";

const ZERO_WIDTH = /[\u200B-\u200D\uFEFF\u2060]/g;
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
const HTML_TAG = /<\/?[^>]+>/g;

/** Severe slurs / hate — matched on normalized text with word boundaries. */
const ABUSE_WORDS = [
  "nigger",
  "nigga",
  "faggot",
  "fag",
  "retard",
  "retarded",
  "kike",
  "spic",
  "chink",
  "tranny",
  "troon",
  "rape",
  "rapist",
  "nazi",
  "hitler",
];

const SEXUAL_ABUSE_WORDS = [
  "porn",
  "pornhub",
  "onlyfans",
  "nude",
  "nudes",
  "xxx",
  "hentai",
  "loli",
  "shota",
];

const HARASS_PATTERNS = [
  /\bkys\b/,
  /\bkill\s+your\s*self\b/,
  /\bkill\s+yourself\b/,
  /\bi\s+will\s+(kill|hurt|rape)\s+you\b/,
  /\bi\s+hope\s+you\s+die\b/,
  /\bgo\s+die\b/,
  /\bi\s+know\s+where\s+you\s+live\b/,
];

const SCAM_PATTERNS = [
  /\b(seed\s*phrase|recovery\s+phrase|mnemonic|private\s*key|secret\s*key)\b/,
  /\bsend\s+(\d+(\.\d+)?\s+)?(sol|eth|usdc|usdt)\b/,
  /\b(double|2x|3x)\s+your\s+(sol|money|crypto)\b/,
  /\bdm\s+me\s+(for|to)\s+(airdrop|whitelist|free|profit)/,
  /\b(airdrop|whitelist)\s+(claim|connect|wallet)\b/,
  /\bconnect\s+(your\s+)?wallet\s+(to|and)\s+(claim|receive|unlock)/,
  /\b(whatsapp|watsapp)\s*[:./]/,
  /\bfree\s+(sol|eth|usdc|tokens?)\s+(if|when)\s+you\b/,
];

const URL_RE = /https?:\/\/[^\s]+|www\.[^\s]+|t\.me\/[^\s]+|discord\.gg\/[^\s]+/gi;
const SHORTENER_RE = /\b(bit\.ly|tinyurl\.com|t\.co|rb\.gy|cutt\.ly|is\.gd|ow\.ly)\b/i;

function collapseRepeats(s) {
  return s.replace(/(.)\1{2,}/g, "$1$1");
}

function leetNormalize(s) {
  return s
    .replace(/[@4]/g, "a")
    .replace(/0/g, "o")
    .replace(/[1!|]/g, "i")
    .replace(/3/g, "e")
    .replace(/5\$/g, "s")
    .replace(/7/g, "t")
    .replace(/\$/g, "s");
}

export function normalizeForAbuseCheck(raw) {
  const lower = String(raw || "")
    .toLowerCase()
    .replace(ZERO_WIDTH, "")
    .replace(/[_*~`'".,!?;:()[\]{}]/g, " ");
  return collapseRepeats(leetNormalize(lower)).replace(/\s+/g, " ").trim();
}

export function sanitizeCommentText(raw) {
  return String(raw || "")
    .replace(ZERO_WIDTH, "")
    .replace(CONTROL_CHARS, "")
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(HTML_TAG, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, COMMENT_TEXT_MAX);
}

export function sanitizeDisplayName(raw) {
  const cleaned = String(raw || "")
    .replace(ZERO_WIDTH, "")
    .replace(CONTROL_CHARS, "")
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(HTML_TAG, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, COMMENT_NAME_MAX);
  return cleaned || DEFAULT_COMMENT_NAME;
}

/** Allow only http(s) profile image URLs. Empty means use Doctor Token fallback. */
export function sanitizeAvatarUrl(raw) {
  const url = String(raw || "").trim();
  if (!url || url.length > COMMENT_AVATAR_MAX) return "";
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return "";
    if (parsed.username || parsed.password) return "";
    return parsed.toString().slice(0, COMMENT_AVATAR_MAX);
  } catch {
    return "";
  }
}

const GUEST_ADJECTIVES = [
  "Amber",
  "Coral",
  "Swift",
  "Quiet",
  "Bright",
  "Calm",
  "Bold",
  "Lucky",
  "Noble",
  "Rapid",
  "Solar",
  "Misty",
  "Frost",
  "Sage",
  "Keen",
  "Lunar",
  "Maple",
  "River",
];

const GUEST_ANIMALS = [
  "Fox",
  "Otter",
  "Wren",
  "Hawk",
  "Lynx",
  "Seal",
  "Finch",
  "Puma",
  "Crane",
  "Wolf",
  "Bear",
  "Dove",
  "Koala",
  "Heron",
];

function randomInt(max) {
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    return buf[0] % max;
  }
  return Math.floor(Math.random() * max);
}

function randomId(len) {
  const chars = "abcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < len; i += 1) out += chars[randomInt(chars.length)];
  return out;
}

/** Safe unique guest name, e.g. CoralFox-k7m. Always within COMMENT_NAME_MAX. */
export function generateGuestDisplayName() {
  const adj = GUEST_ADJECTIVES[randomInt(GUEST_ADJECTIVES.length)];
  const animal = GUEST_ANIMALS[randomInt(GUEST_ANIMALS.length)];
  return `${adj}${animal}-${randomId(3)}`.slice(0, COMMENT_NAME_MAX);
}

function hasBlockedWord(normalized, words) {
  return words.some((w) => new RegExp(`\\b${w}\\b`, "i").test(normalized));
}

function abuseReasonForNormalized(normalized, raw) {
  if (hasBlockedWord(normalized, ABUSE_WORDS) || hasBlockedWord(normalized, SEXUAL_ABUSE_WORDS)) {
    return "abuse";
  }
  if (HARASS_PATTERNS.some((re) => re.test(normalized))) return "abuse";
  if (SCAM_PATTERNS.some((re) => re.test(normalized))) return "scam";
  if (SHORTENER_RE.test(raw) || (raw.match(URL_RE) || []).length > 0) return "links";
  return null;
}

function isSpammyNoise(raw) {
  const text = String(raw || "").trim();
  if (!text) return true;
  if (/^[\s.,!?;:()[\]{}'"`~@#$%^&*_\-+=/\\|<>]+$/.test(text)) return true;
  if (/(.)\1{6,}/.test(text)) return true;

  const compactLetters = text.toLowerCase().replace(/[^a-z]/g, "");
  if (compactLetters.length >= 12) {
    const vowels = (compactLetters.match(/[aeiou]/g) || []).length;
    if (vowels / compactLetters.length < 0.18) return true;
  }

  const tokens = text.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length >= 5) {
    const uniqueCount = new Set(tokens).size;
    if (uniqueCount <= 2) return true;
  }
  return false;
}

/**
 * @param {string} raw
 * @returns {{ ok: true, displayName: string } | { ok: false, reason: string }}
 */
export function filterCommunityDisplayName(raw) {
  const cleaned = String(raw || "")
    .replace(ZERO_WIDTH, "")
    .replace(CONTROL_CHARS, "")
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(HTML_TAG, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, COMMENT_NAME_MAX);

  if (!cleaned || cleaned.length < COMMENT_TEXT_MIN) {
    return { ok: false, reason: "empty" };
  }

  const abuse = abuseReasonForNormalized(normalizeForAbuseCheck(cleaned), cleaned);
  if (abuse) return { ok: false, reason: abuse };
  if (isSpammyNoise(cleaned)) return { ok: false, reason: "spam" };
  return { ok: true, displayName: cleaned };
}

/**
 * @param {string} text
 * @param {string} [displayName]
 * @param {string} [avatarUrl]
 * @returns {{ ok: true, text: string, displayName: string, avatarUrl: string } | { ok: false, reason: string }}
 */
export function filterCommunityComment(text, displayName, avatarUrl) {
  const cleanText = sanitizeCommentText(text);
  const cleanName = sanitizeDisplayName(displayName);
  const cleanAvatar = sanitizeAvatarUrl(avatarUrl);

  if (!cleanText) {
    return { ok: false, reason: "empty" };
  }
  if (cleanText.length < COMMENT_TEXT_MIN) {
    return { ok: false, reason: "too_short" };
  }

  const named = filterCommunityDisplayName(cleanName);
  if (!named.ok) {
    return { ok: false, reason: named.reason };
  }

  const textCheck = normalizeForAbuseCheck(cleanText);
  const textAbuse = abuseReasonForNormalized(textCheck, cleanText);
  if (textAbuse) {
    return { ok: false, reason: textAbuse };
  }

  const urls = cleanText.match(URL_RE) || [];
  if (urls.length > 1) {
    return { ok: false, reason: "links" };
  }
  if (urls.length === 1 && cleanText.replace(URL_RE, "").trim().length < COMMENT_TEXT_MIN) {
    return { ok: false, reason: "links" };
  }

  if (isSpammyNoise(cleanText)) {
    return { ok: false, reason: "spam" };
  }

  return { ok: true, text: cleanText, displayName: named.displayName, avatarUrl: cleanAvatar };
}

export function publicNameError(reason) {
  if (reason === "empty" || reason === "too_short") {
    return "Enter a name with at least 2 characters.";
  }
  if (reason === "links") {
    return "Names cannot include links.";
  }
  if (reason === "scam") {
    return "That name looks like a scam or phishing attempt.";
  }
  if (reason === "spam") {
    return "That name looks like spam. Please choose another.";
  }
  return "That name was blocked. Please keep it respectful.";
}

export function publicCommentError(reason) {
  if (reason === "empty" || reason === "too_short") {
    return "Write a short comment before posting.";
  }
  if (reason === "links") {
    return "That comment has too many or blocked links.";
  }
  if (reason === "scam") {
    return "That comment looks like a scam or phishing attempt.";
  }
  if (reason === "spam") {
    return "That comment looks like spam. Please write a clear message.";
  }
  return "That comment was blocked. Please keep it respectful.";
}
